/**
 * ComponentRegistryModal - Shows all logged components with statistics
 * 
 * Displays a hierarchical tree view of all components being monitored,
 * with log counts, level breakdowns, and last-seen timestamps.
 * Click a component to filter the main log table.
 * 
 * Enhanced with:
 * - Source files tab showing files using EdgeLogger
 * - Search/filter functionality
 * - Error rate and avg duration stats
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { X, ChevronRight, ChevronDown, RefreshCw, Filter, Search, FileCode, BarChart3, ExternalLink, Copy } from 'lucide-react';
import { useLoggingConfig } from '../../../hooks/useLoggingConfig';

type TabType = 'components' | 'sources';

// Dynamic source file data from backend
interface SourceFileStat {
  file: string;
  logCount: number;
  lastSeen: string;
  components: string[];
  levelCounts: {
    debug: number;
    info: number;
    warn: number;
    error: number;
    performance: number;
  };
}

interface ComponentStat {
  component: string;
  logCount: number;
  lastSeen: string;
  levelCounts: {
    debug: number;
    info: number;
    warn: number;
    error: number;
    performance: number;
  };
}

interface ComponentRegistryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectComponent: (component: string) => void;
}

interface TreeNode {
  name: string;
  fullPath: string;
  stats?: ComponentStat;
  children: Map<string, TreeNode>;
}

function buildTree(stats: ComponentStat[]): TreeNode {
  const root: TreeNode = {
    name: 'root',
    fullPath: '',
    children: new Map(),
  };

  for (const stat of stats) {
    const parts = stat.component.split('.');
    let current = root;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      const fullPath = parts.slice(0, i + 1).join('.');

      if (!current.children.has(part)) {
        current.children.set(part, {
          name: part,
          fullPath,
          children: new Map(),
        });
      }

      current = current.children.get(part)!;

      // Attach stats to the leaf node
      if (i === parts.length - 1) {
        current.stats = stat;
      }
    }
  }

  return root;
}

function formatLastSeen(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return date.toLocaleDateString();
}

/** Recursively sum log counts for a tree node and all descendants */
function getNodeLogCount(node: TreeNode): number {
  let total = node.stats?.logCount || 0;
  node.children.forEach(child => { total += getNodeLogCount(child); });
  return total;
}

interface TreeNodeComponentProps {
  node: TreeNode;
  depth: number;
  onSelect: (component: string) => void;
  isLoggingEnabled: (component: string) => boolean;
  onToggleLogging: (component: string, enabled: boolean) => void;
  isUpdating: string | null;
}

function TreeNodeComponent({ node, depth, onSelect, isLoggingEnabled, onToggleLogging, isUpdating }: TreeNodeComponentProps) {
  // Default: collapse everything except depth 0 (root). Users click to expand.
  const [isExpanded, setIsExpanded] = useState(depth === 0);
  const hasChildren = node.children.size > 0;
  const stats = node.stats;
  const isEnabled = isLoggingEnabled(node.fullPath);
  const isCurrentlyUpdating = isUpdating === node.fullPath;

  // Calculate aggregate stats for parent nodes
  const aggregateStats = useMemo(() => {
    if (stats) return null;
    
    let total = 0;
    const collectStats = (n: TreeNode) => {
      if (n.stats) total += n.stats.logCount;
      n.children.forEach(child => collectStats(child));
    };
    collectStats(node);
    return total;
  }, [node, stats]);

  return (
    <div>
      <div
        className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
          depth === 0 ? 'hidden' : ''
        }`}
        style={{ paddingLeft: `${depth * 16}px` }}
        onClick={() => {
          if (hasChildren) {
            setIsExpanded(!isExpanded);
          }
          if (stats) {
            onSelect(node.fullPath);
          }
        }}
      >
        {/* Expand/Collapse Icon */}
        {hasChildren ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>
        ) : (
          <div className="w-5" />
        )}

        {/* Component Name */}
        <span className={`font-medium ${stats ? 'text-gray-900 dark:text-gray-100' : 'text-gray-600 dark:text-gray-400'}`}>
          {node.name}
        </span>

        {/* Stats */}
        {stats && (
          <div className="flex items-center gap-2 ml-auto text-xs">
            {/* Level badges */}
            {stats.levelCounts.error > 0 && (
              <span className="px-1.5 py-0.5 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded">
                {stats.levelCounts.error} err
              </span>
            )}
            {stats.levelCounts.warn > 0 && (
              <span className="px-1.5 py-0.5 bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300 rounded">
                {stats.levelCounts.warn} warn
              </span>
            )}
            {stats.levelCounts.performance > 0 && (
              <span className="px-1.5 py-0.5 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded">
                {stats.levelCounts.performance} perf
              </span>
            )}
            
            {/* Total count */}
            <span className="text-gray-500 dark:text-gray-400">
              {stats.logCount.toLocaleString()} logs
            </span>
            
            {/* Last seen */}
            <span className="text-gray-400 dark:text-gray-500">
              {formatLastSeen(stats.lastSeen)}
            </span>

            {/* Filter button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSelect(node.fullPath);
              }}
              className="p-1 text-lex-accent hover:text-lex-accent-hover"
              title="Filter by this component"
            >
              <Filter className="w-3.5 h-3.5" />
            </button>

            {/* Logging Toggle */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleLogging(node.fullPath, !isEnabled);
              }}
              disabled={isCurrentlyUpdating}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                isEnabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
              } ${isCurrentlyUpdating ? 'opacity-50' : ''}`}
              title={isEnabled ? 'Logging enabled - click to disable' : 'Logging disabled - click to enable'}
            >
              <span
                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                  isEnabled ? 'translate-x-5' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        )}

        {/* Aggregate count for parent nodes */}
        {!stats && aggregateStats !== null && aggregateStats > 0 && (
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {aggregateStats.toLocaleString()} total
            </span>
            {/* Logging Toggle for parent - toggles all children */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleLogging(node.fullPath, !isEnabled);
              }}
              disabled={isCurrentlyUpdating}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                isEnabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
              } ${isCurrentlyUpdating ? 'opacity-50' : ''}`}
              title={isEnabled ? 'Logging enabled - click to disable all' : 'Logging disabled - click to enable all'}
            >
              <span
                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                  isEnabled ? 'translate-x-5' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        )}
      </div>

      {/* Children */}
      {isExpanded && hasChildren && (
        <div>
          {Array.from(node.children.values())
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((child) => (
            <TreeNodeComponent
              key={child.fullPath}
              node={child}
              depth={depth + 1}
              onSelect={onSelect}
              isLoggingEnabled={isLoggingEnabled}
              onToggleLogging={onToggleLogging}
              isUpdating={isUpdating}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function ComponentRegistryModal({
  isOpen,
  onClose,
  onSelectComponent,
}: ComponentRegistryModalProps) {
  const [stats, setStats] = useState<ComponentStat[]>([]);
  const [sourceFiles, setSourceFiles] = useState<SourceFileStat[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSources, setIsLoadingSources] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('components');
  const [searchQuery, setSearchQuery] = useState('');
  const [isUpdatingComponent, setIsUpdatingComponent] = useState<string | null>(null);

  // Logging config hook - realm comes from JWT automatically
  const { isEnabled: isLoggingEnabled, updateConfig } = useLoggingConfig();

  const handleToggleLogging = useCallback(async (component: string, enabled: boolean) => {
    setIsUpdatingComponent(component);
    try {
      await updateConfig(component, enabled);
    } catch (err) {
      console.error('[ComponentRegistry] Failed to toggle logging:', err);
    } finally {
      setIsUpdatingComponent(null);
    }
  }, [updateConfig]);

  const fetchStats = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('webelieve_access_token');
      const response = await fetch('/api/logs/components/stats', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setStats(data.stats || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSourceFiles = async () => {
    setIsLoadingSources(true);
    try {
      const token = localStorage.getItem('webelieve_access_token');
      const response = await fetch('/api/logs/sources', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setSourceFiles(data.sources || []);
    } catch (err) {
      console.error('[ComponentRegistry] Failed to fetch source files:', err);
    } finally {
      setIsLoadingSources(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchStats();
      fetchSourceFiles();
    }
  }, [isOpen]);

  // Filter stats by search query
  const filteredStats = useMemo(() => {
    if (!searchQuery.trim()) return stats;
    const query = searchQuery.toLowerCase();
    return stats.filter(s => s.component.toLowerCase().includes(query));
  }, [stats, searchQuery]);

  const tree = useMemo(() => buildTree(filteredStats), [filteredStats]);

  const totalLogs = useMemo(() => 
    stats.reduce((sum, s) => sum + s.logCount, 0),
    [stats]
  );

  // Calculate aggregate statistics
  const aggregateStats = useMemo(() => {
    const totalErrors = stats.reduce((sum, s) => sum + s.levelCounts.error, 0);
    const totalWarns = stats.reduce((sum, s) => sum + s.levelCounts.warn, 0);
    const totalPerf = stats.reduce((sum, s) => sum + s.levelCounts.performance, 0);
    const errorRate = totalLogs > 0 ? ((totalErrors / totalLogs) * 100).toFixed(2) : '0';
    return { totalErrors, totalWarns, totalPerf, errorRate };
  }, [stats, totalLogs]);

  // Filter source files by search query
  const filteredSourceFiles = useMemo(() => {
    if (!searchQuery.trim()) return sourceFiles;
    const query = searchQuery.toLowerCase();
    return sourceFiles.filter(f => 
      f.file.toLowerCase().includes(query) || 
      f.components.some(c => c.toLowerCase().includes(query))
    );
  }, [sourceFiles, searchQuery]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSelect = (component: string) => {
    onSelectComponent(component);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Component Registry
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {stats.length} components • {totalLogs.toLocaleString()} logs (last 24h)
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={fetchStats}
              disabled={isLoading}
              className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Stats Summary Bar */}
        <div className="flex items-center gap-4 px-6 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 text-xs">
          <div className="flex items-center gap-1">
            <BarChart3 className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-gray-500 dark:text-gray-400">Error Rate:</span>
            <span className={`font-medium ${parseFloat(aggregateStats.errorRate) > 1 ? 'text-red-600' : 'text-green-600'}`}>
              {aggregateStats.errorRate}%
            </span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-gray-500 dark:text-gray-400">Errors:</span>
            <span className="font-medium text-red-600">{aggregateStats.totalErrors}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-gray-500 dark:text-gray-400">Warnings:</span>
            <span className="font-medium text-yellow-600">{aggregateStats.totalWarns}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-gray-500 dark:text-gray-400">Perf:</span>
            <span className="font-medium text-green-600">{aggregateStats.totalPerf}</span>
          </div>
        </div>

        {/* Tabs and Search */}
        <div className="flex items-center justify-between px-6 py-2 border-b border-gray-200 dark:border-gray-700">
          {/* Tabs */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setActiveTab('components')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${
                activeTab === 'components'
                  ? 'bg-lex-accent-soft text-lex-accent'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <BarChart3 className="w-4 h-4" />
              Components
            </button>
            <button
              onClick={() => setActiveTab('sources')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${
                activeTab === 'sources'
                  ? 'bg-lex-accent-soft text-lex-accent'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <FileCode className="w-4 h-4" />
              Source Files
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lex-accent w-48"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {error && (
            <div className="text-center text-red-500 py-8">
              Error: {error}
            </div>
          )}

          {/* Components Tab */}
          {activeTab === 'components' && (
            <>
              {!error && stats.length === 0 && !isLoading && (
                <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                  No components logged in the last 24 hours
                </div>
              )}

              {!error && filteredStats.length === 0 && stats.length > 0 && (
                <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                  No components match "{searchQuery}"
                </div>
              )}

              {!error && filteredStats.length > 0 && (
                <div className="space-y-1">
                  {Array.from(tree.children.values())
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((node) => (
                    <TreeNodeComponent
                      key={node.fullPath}
                      node={node}
                      depth={1}
                      onSelect={handleSelect}
                      isLoggingEnabled={isLoggingEnabled}
                      onToggleLogging={handleToggleLogging}
                      isUpdating={isUpdatingComponent}
                    />
                  ))}
                </div>
              )}

              {isLoading && stats.length === 0 && (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              )}
            </>
          )}

          {/* Source Files Tab */}
          {activeTab === 'sources' && (
            <div className="space-y-2">
              {isLoadingSources && sourceFiles.length === 0 ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : filteredSourceFiles.length === 0 ? (
                <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                  {searchQuery ? `No source files match "${searchQuery}"` : 'No source files have logged in the last 24 hours'}
                </div>
              ) : (
                filteredSourceFiles.map((file) => (
                  <div
                    key={file.file}
                    className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <FileCode className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 font-mono truncate" title={file.file}>
                          {file.file}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                          <span>{file.logCount.toLocaleString()} logs</span>
                          <span>•</span>
                          <span>{formatLastSeen(file.lastSeen)}</span>
                          {file.levelCounts.error > 0 && (
                            <>
                              <span>•</span>
                              <span className="text-red-500">{file.levelCounts.error} errors</span>
                            </>
                          )}
                          {file.levelCounts.warn > 0 && (
                            <>
                              <span>•</span>
                              <span className="text-yellow-500">{file.levelCounts.warn} warns</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* Component badges */}
                      {file.components.slice(0, 2).map((comp) => (
                        <span 
                          key={comp}
                          className="px-2 py-0.5 text-xs bg-lex-accent-soft text-lex-accent rounded cursor-pointer hover:bg-lex-state-hover"
                          onClick={() => handleSelect(comp)}
                          title={`Filter by ${comp}`}
                        >
                          {comp}
                        </span>
                      ))}
                      {file.components.length > 2 && (
                        <span className="text-xs text-gray-400">+{file.components.length - 2}</span>
                      )}
                      <button
                        onClick={() => navigator.clipboard.writeText(file.file)}
                        className="p-1 text-gray-400 hover:text-lex-accent transition-colors"
                        title="Copy file path"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <a
                        href={`vscode://vscode-remote/ssh-remote+edgelexllc/home/edgelexllc/projects/edgelexapp/webelieve-frontend/${file.file}`}
                        className="p-1 text-gray-400 hover:text-lex-accent transition-colors"
                        title="Open in VS Code (SSH Remote)"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                      <button
                        onClick={() => handleSelect(file.components[0] || '')}
                        className="p-1 text-gray-400 hover:text-lex-accent transition-colors"
                        title="Filter logs by primary component"
                      >
                        <Filter className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
          Click a component to filter logs • Toggle switches enable/disable logging per component
        </div>
      </div>
    </div>
  );
}
